/** Vertragstypen der API. Spiegeln die serverseitigen Antwortformate wider. */

export const PERMISSIONS = {
  DashboardRead: 'dashboard:read',
  ReportRead: 'report:read',
  ReportExport: 'report:export',
  SyncRead: 'sync:read',
  SyncTrigger: 'sync:trigger',
  PriceWrite: 'price:write',
  ProviderRead: 'provider:read',
  ProviderWrite: 'provider:write',
  AuditRead: 'audit:read',
  SettingsWrite: 'settings:write',
  IngestWrite: 'ingest:write',
  CatalogRead: 'catalog:read',
  CatalogWrite: 'catalog:write',
  AssetRead: 'asset:read',
  AssetManage: 'asset:manage',
  LicenseReadOwn: 'license:read-own',
  /** Aus dem Katalog anfragen (Warenkorb absenden). */
  CatalogRequest: 'catalog:request',
  /** Bestellen ohne Freigabe – Teamleitung und Geschäftsleitung. */
  CatalogRequestDirect: 'catalog:request-direct',
  /** Alle Anfragen einsehen und erneut einreichen. */
  CatalogRequestManage: 'catalog:request-manage',
  /** Antraege auf neue Benutzer stellen (Rolle UserRequester). */
  UserRequestCreate: 'user-request:create',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export interface MeResponse {
  oid: string;
  userPrincipalName: string | null;
  displayName: string | null;
  tenantId: string;
  roles: string[];
  permissions: Permission[];
}

export interface SkuSummary {
  skuId: string;
  providerId: string;
  providerSlug: string;
  partNumber: string;
  displayName: string;
  totalUnits: number;
  assignedUnits: number;
  availableUnits: number;
  assignedToDisabled: number;
  assignedInactive: number | null;
  warningUnits: number;
  suspendedUnits: number;
  utilizationPercent: number;
  capturedAt: string;
  isFree: boolean;
  allocation: CostAllocationKind;
  category: string | null;
  /** Vom Anbieter gemeldete Vergabe, unabhaengig von excludeDisabled. */
  reportedAssignedUnits: number;
  unitCost: number | null;
  currency: string | null;
  monthlyCostAssigned: number | null;
  monthlyCostAvailable: number | null;
  monthlyCostUnused: number | null;
}

export type CostAllocationKind = 'direct' | 'shared';
export type AllocationKey = 'heads' | 'directCost';

/** Produktkategorien – Schluessel wie im Backend (api/src/lib/categories.ts). */
export const LICENSE_CATEGORIES = [
  'productivity',
  'security',
  'communication',
  'infrastructure',
  'erp',
  'data',
  'ai',
  'development',
  'creative',
  'other',
] as const;
export type LicenseCategory = (typeof LICENSE_CATEGORIES)[number];

export interface CategoryCost {
  category: string | null;
  monthlyCost: number;
  skuCount: number;
  unpricedSkuCount: number;
}

export interface CostTotals {
  monthlyCostAssigned: number;
  monthlyCostAvailable: number;
  monthlyCostUnused: number;
  /** Gemeinkosten: Bestand × Preis aller nicht zuweisbaren Produkte. */
  monthlyCostShared: number;
  monthlyCostTotal: number;
  byCategory: CategoryCost[];
  unpricedSkuCount: number;
  pricedSkuCount: number;
  currency: string | null;
  mixedCurrencies: boolean;
}

export interface DashboardSummary {
  capturedAt: string | null;
  syncRunId: string | null;
  totals: {
    totalUnits: number;
    assignedUnits: number;
    availableUnits: number;
    assignedToDisabled: number;
    assignedInactive: number | null;
    skuCount: number;
    utilizationPercent: number;
    hiddenFreeSkuCount: number;
    excludedDisabledUnits: number;
    sharedSkuCount: number;
  };
  costs: CostTotals;
  skus: SkuSummary[];
  providers: ProviderSummary[];
}

/**
 * Kennzahlen einer Lizenzquelle. Stückzahlen zählen wie in den Gesamtzahlen
 * nur zuweisbare Produkte; `monthlyCost` ist alles, was die Quelle kostet –
 * einschließlich ihrer Gemeinkosten.
 */
export interface ProviderSummary {
  id: string;
  slug: string;
  displayName: string;
  connectorKey: string;
  enabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  skuCount: number;
  sharedSkuCount: number;
  totalUnits: number;
  assignedUnits: number;
  availableUnits: number;
  monthlyCost: number | null;
  currency: string | null;
  unpricedSkuCount: number;
}

export interface TrendPoint {
  capturedAt: string;
  totalUnits: number;
  assignedUnits: number;
  availableUnits: number;
}

export interface CountryReportRow {
  countryCode: string;
  countryName: string;
  assignedUnits: number;
  enabledUnits: number;
  disabledUnits: number;
  monthlyCost: number | null;
  unpricedSkuCount: number;
  currency: string | null;
  /** Personen mit mindestens einer Lizenz – Umlageschlüssel „Köpfe“. */
  heads: number;
  allocationShare: number | null;
  sharedCostAllocated: number | null;
  monthlyCostTotal: number | null;
  skus: Array<{
    skuId: string;
    partNumber: string;
    displayName: string;
    assignedUnits: number;
    isFree: boolean;
    monthlyCost: number | null;
  }>;
}

/** Gemeinkosten-Topf mit Ergebnis der Umlage. */
export interface SharedCostPool {
  allocationKey: AllocationKey;
  monthlyCost: number;
  currency: string | null;
  mixedCurrencies: boolean;
  unpricedSkuCount: number;
  allocatedTotal: number;
  skus: Array<{
    skuId: string;
    providerSlug: string;
    partNumber: string;
    displayName: string;
    totalUnits: number;
    unitCost: number | null;
    currency: string | null;
    monthlyCost: number | null;
    allocationCountries: string[];
  }>;
}

export interface CountryReport {
  rows: CountryReportRow[];
  /** null bei Filter auf ein einzelnes Produkt. */
  sharedCosts: SharedCostPool | null;
}

/** Produkt mit Preisangaben, wie es der Administrationsbereich pflegt. */
/**
 * Eine Kostenposition: ein Bezugsweg (EA/Bechtle, Vodafone, Direktkauf ...) mit
 * eigenem Preis und eigener Menge. `units: null` deckt alle uebrigen Einheiten ab.
 */
export interface CostItemRow {
  id: string;
  skuId: string;
  source: string;
  contractRef: string | null;
  units: number | null;
  unitCost: number;
  currency: string;
  validFrom: string | null;
  validTo: string | null;
  note: string | null;
  updatedAt: string;
  updatedBy: string | null;
  /** false, wenn die Position heute ausserhalb ihrer Laufzeit liegt. */
  active: boolean;
}

export interface CostItemInput {
  source: string;
  contractRef?: string | null;
  units?: number | null;
  unitCost: number;
  currency?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  note?: string | null;
}

export interface ProductPriceRow {
  skuId: string;
  providerId: string;
  providerSlug: string;
  providerName: string;
  partNumber: string;
  displayName: string;
  isFree: boolean;
  allocation: CostAllocationKind;
  /** Ziel-Länder der Umlage (ISO-2); leer = alle Länder. */
  allocationCountries: string[];
  category: LicenseCategory | null;
  unitCost: number | null;
  currency: string | null;
  priceUpdatedAt: string | null;
  priceUpdatedBy: string | null;
  totalUnits: number | null;
  assignedUnits: number | null;
  retired: boolean;
  costItems: CostItemRow[];
  sources: string[];
  coveredUnits: number;
  uncoveredUnits: number;
  contractedMonthlyCost: number | null;
  mixedCurrencies: boolean;
}

export interface PersonLicense {
  providerSlug: string;
  providerName: string;
  skuId: string;
  partNumber: string;
  displayName: string;
  isFree: boolean;
  /** Gemeinkosten: kostet die Person nichts, der Betrag steht im Gemeinkosten-Topf. */
  shared: boolean;
  unitCost: number | null;
  currency: string | null;
  monthlyCost: number | null;
}

/** Eine Person ueber alle Lizenzquellen, zusammengefuehrt ueber E-Mail bzw. Anmeldename. */
export interface PersonRow {
  personKey: string;
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
  countryCode: string;
  countryName: string;
  department: string | null;
  companyName: string | null;
  accountEnabled: boolean;
  providers: string[];
  licenseCount: number;
  monthlyCost: number | null;
  unpricedCount: number;
  currency: string | null;
  licenses: PersonLicense[];
  /** Zugeordnete, nicht entfernte Geraete. */
  deviceCount: number;
  /** Anschaffungswert, EINMALIG - nie mit monthlyCost verrechnen. */
  hardwareCost: number | null;
  unpricedDeviceCount: number;
  hardwareCurrency: string | null;
}

export interface PeopleTotals {
  personCount: number;
  monthlyCost: number;
  unpricedPersonCount: number;
  deviceCount: number;
  /** Einmalig, nicht monatlich. */
  hardwareCost: number;
  unpricedDeviceCount: number;
  currency: string | null;
  mixedCurrencies: boolean;
}

export interface PeopleResponse extends Paged<PersonRow> {
  totals: PeopleTotals;
}

/** Gemeinsame Filter der Auswertungen. */
export interface ReportFilter {
  providerId?: string;
  excludeFree?: boolean;
  excludeDisabled?: boolean;
}

export interface AssignmentRow {
  assignmentId: string;
  providerSlug: string;
  providerName: string;
  skuPartNumber: string;
  skuDisplayName: string;
  userPrincipalName: string | null;
  displayName: string | null;
  mail: string | null;
  accountEnabled: boolean;
  countryCode: string;
  countryName: string;
  city: string | null;
  department: string | null;
  jobTitle: string | null;
  source: 'DIRECT' | 'GROUP' | 'UNKNOWN';
  assignedAt: string | null;
  lastSignInAt: string | null;
}

export type SyncStatus = 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PARTIAL' | 'CANCELLED';
export type SyncTrigger = 'SCHEDULED' | 'MANUAL' | 'BACKFILL' | 'INGEST';

export interface SyncRunRow {
  id: string;
  providerId: string;
  providerName: string;
  providerSlug: string;
  connectorKey: string;
  trigger: SyncTrigger;
  status: SyncStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  skuCount: number;
  accountCount: number;
  assignmentCount: number;
  warnings: string[];
  errorMessage: string | null;
  triggeredByUpn: string | null;
  correlationId: string;
}

export type SyncEventLevel = 'INFO' | 'WARN' | 'ERROR';

/** Ein Abschnitt eines Laufs, wie er im Protokoll der Sync-Seite erscheint. */
export interface SyncRunEvent {
  id: number;
  at: string;
  level: SyncEventLevel;
  /** Technischer Abschnittsschluessel, z. B. "fetch.skus" – nur fuer Diagnose. */
  step: string;
  message: string;
  detail: Record<string, unknown> | null;
}

export interface SyncRunDetail extends Omit<SyncRunRow, 'providerSlug' | 'connectorKey' | 'triggeredByUpn'> {
  events: SyncRunEvent[];
}

export interface SyncStatusResponse {
  running: Array<{ id: string; providerId: string; startedAt: string; trigger: SyncTrigger }>;
  lastCompleted: {
    id: string;
    status: SyncStatus;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
  } | null;
  scheduleCron: string;
}

export interface ConnectorConfigField {
  key: string;
  label: string;
  type: 'string' | 'boolean' | 'number' | 'select' | 'licenseItems' | 'memberList' | 'seatGroups';
  required: boolean;
  description?: string;
  options?: Array<{ value: string; label: string }>;
  defaultValue?: string | number | boolean;
  secretReference?: boolean;
  /** Nur anzeigen, wenn ein anderes Feld diesen Wert hat. */
  visibleWhen?: { key: string; equals?: string | boolean; notEquals?: string | boolean };
}

export interface ConnectorDescriptor {
  key: string;
  displayName: string;
  description: string;
  status: 'available' | 'preview' | 'planned';
  requiredPermissions: string[];
  configFields: ConnectorConfigField[];
  supportsCountryReporting: boolean;
  /** Einrichtungsschritte, im Administrationsbereich angezeigt. */
  setupSteps?: string[];
  docsUrl?: string;
}

export interface ProviderRow {
  id: string;
  connectorKey: string;
  slug: string;
  displayName: string;
  description: string | null;
  enabled: boolean;
  config: Record<string, unknown>;
  scheduleCron: string | null;
  lastSyncAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  /** Nur bei manuell gepflegten Quellen: Ergebnis des Sofortlaufs beim Speichern. */
  sync?: { status: string; errorMessage?: string };
}

export interface AuditLogRow {
  id: string;
  occurredAt: string;
  actorOid: string;
  actorUpn: string | null;
  actorRoles: string[];
  action: string;
  targetType: string | null;
  targetId: string | null;
  outcome: 'SUCCESS' | 'FAILURE' | 'DENIED';
  metadata: Record<string, unknown>;
  ipAddress: string | null;
  correlationId: string | null;
}

export interface Paged<T> {
  total: number;
  skip: number;
  take: number;
  rows: T[];
}

// --- IT-Katalog (Hardware und Software) -------------------------------------

export type CatalogItemKind = 'HARDWARE' | 'SOFTWARE';
export const CATALOG_ITEM_KINDS: CatalogItemKind[] = ['HARDWARE', 'SOFTWARE'];

export type CatalogSupportStatus = 'STANDARD' | 'SUPPORTED' | 'LIMITED' | 'PHASE_OUT' | 'NOT_ORDERABLE';
export const CATALOG_SUPPORT_STATUS: CatalogSupportStatus[] = [
  'STANDARD',
  'SUPPORTED',
  'LIMITED',
  'PHASE_OUT',
  'NOT_ORDERABLE',
];

export type CatalogPublishStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export const CATALOG_PUBLISH_STATUS: CatalogPublishStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
export type CatalogPriceInterval = 'ONE_TIME' | 'MONTHLY' | 'YEARLY';
export const CATALOG_PRICE_INTERVAL: CatalogPriceInterval[] = ['ONE_TIME', 'MONTHLY', 'YEARLY'];

/** Eine technische Angabe; die englische Fassung ist optional. */
export interface CatalogSpecification {
  label: string;
  value: string;
  labelEn?: string;
  valueEn?: string;
}

export interface CatalogCategoryRow {
  id: string;
  slug: string;
  kind: CatalogItemKind;
  name: string;
  nameEn: string | null;
  description: string | null;
  descriptionEn: string | null;
  imageId: string | null;
  sortOrder: number;
  isActive: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  /** Zahl der für die abrufende Person sichtbaren Einträge. */
  itemCount?: number;
}

export interface CatalogItemRow {
  id: string;
  categoryId: string;
  name: string;
  nameEn: string | null;
  manufacturer: string;
  modelNumber: string | null;
  shortDescription: string | null;
  shortDescriptionEn: string | null;
  description: string | null;
  descriptionEn: string | null;
  imageId: string | null;
  specifications: CatalogSpecification[];
  operatingSystems: string[];
  intendedUse: string | null;
  intendedUseEn: string | null;
  audience: string | null;
  audienceEn: string | null;
  orderNote: string | null;
  orderNoteEn: string | null;
  orderUrl: string | null;
  /** Zusatzangabe bei der Bestellung (Opt-in), z. B. Gerätemodell. */
  orderFieldEnabled: boolean;
  orderFieldLabel: string | null;
  orderFieldLabelEn: string | null;
  orderFieldHint: string | null;
  orderFieldHintEn: string | null;
  orderFieldRequired: boolean;
  /** Dezimalzahl als Text, wie sie der Server liefert. */
  price: string | null;
  currency: string | null;
  priceInterval: CatalogPriceInterval;
  supportStatus: CatalogSupportStatus;
  publishStatus: CatalogPublishStatus;
  sortOrder: number;
  archivedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  category: { id: string; slug: string; kind: CatalogItemKind; name: string; nameEn: string | null };
}

export interface CatalogItemsResponse extends Paged<CatalogItemRow> {
  /** Hersteller der sichtbaren Einträge – Grundlage der Filterauswahl. */
  manufacturers: string[];
}

export interface CatalogCategoryInput {
  kind?: CatalogItemKind;
  name: string;
  nameEn?: string | null;
  slug?: string;
  description?: string | null;
  descriptionEn?: string | null;
  imageId?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CatalogItemInput {
  categoryId: string;
  name: string;
  nameEn?: string | null;
  manufacturer: string;
  modelNumber?: string | null;
  shortDescription?: string | null;
  shortDescriptionEn?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  imageId?: string | null;
  specifications?: CatalogSpecification[];
  operatingSystems?: string[];
  intendedUse?: string | null;
  intendedUseEn?: string | null;
  audience?: string | null;
  audienceEn?: string | null;
  orderNote?: string | null;
  orderNoteEn?: string | null;
  orderUrl?: string | null;
  orderFieldEnabled?: boolean;
  orderFieldLabel?: string | null;
  orderFieldLabelEn?: string | null;
  orderFieldHint?: string | null;
  orderFieldHintEn?: string | null;
  orderFieldRequired?: boolean;
  price?: number | null;
  currency?: string | null;
  priceInterval?: CatalogPriceInterval;
  supportStatus?: CatalogSupportStatus;
  publishStatus?: CatalogPublishStatus;
  sortOrder?: number;
}

export interface CatalogImageRow {
  id: string;
  contentType: string;
  byteSize: number;
  checksum: string;
  fileName: string | null;
  createdAt: string;
}

/** Zustand eines Geräts im Bestand. */
export const ASSET_STATUS = ['IN_USE', 'IN_STOCK', 'RESERVED', 'IN_REPAIR', 'RETIRED', 'LOST'] as const;
export type AssetStatus = (typeof ASSET_STATUS)[number];

export interface AssetRow {
  id: string;
  /** Quellsystem, aus dem das Gerät stammt – heute „acmp“. */
  source: string;
  externalId: string;
  assetTag: string | null;
  serialNumber: string | null;
  name: string;
  manufacturer: string | null;
  model: string | null;
  deviceType: string | null;
  catalogItemId: string | null;
  status: AssetStatus;
  accountId: string | null;
  /** Vom Quellsystem gemeldeter Besitzer, unverändert – auch wenn die
   *  Zuordnung zu einer Person fehlschlug. */
  holderRaw: string | null;
  countryCode: string | null;
  location: string | null;
  /** Kostenstelle aus dem Quellsystem; in ACMP oft ein Gesellschaftsname. */
  costCenter: string | null;
  /** Asset Collection, voller Pfad („Tische\\Tisch-0001“); null = kein Kollektionsgerät. */
  collectionPath: string | null;
  /** Ausgabedatum; null, solange weder Quelle noch AudIT es kennen. */
  assignedAt: string | null;
  purchaseDate: string | null;
  warrantyEnd: string | null;
  purchaseCost: string | null;
  currency: string | null;
  notes: string | null;
  lastSeenAt: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  account: {
    id: string;
    displayName: string | null;
    mail: string | null;
    userPrincipalName: string | null;
    countryCode: string | null;
  } | null;
  catalogItem: { id: string; name: string; nameEn: string | null; manufacturer: string | null } | null;
}

export interface AssetHistoryRow {
  id: string;
  accountId: string | null;
  holderRaw: string | null;
  collectionPath: string | null;
  status: AssetStatus;
  fromAt: string;
  /** null = laufender Abschnitt. */
  toAt: string | null;
  changedBy: string | null;
  note: string | null;
  account: { displayName: string | null; mail: string | null; userPrincipalName: string | null } | null;
}

export interface AssetDetail extends AssetRow {
  /** Nur für die Verwaltung gefüllt: Die Historie nennt die Vorbesitzer. */
  history: AssetHistoryRow[];
}

export interface AssetsResponse extends Paged<AssetRow> {
  /** Ob die angemeldete Person den gesamten Bestand sieht oder nur die eigenen Geräte. */
  canManage: boolean;
}

export interface AssetSummary {
  total: number;
  byStatus: Partial<Record<AssetStatus, number>>;
  /** Geräte mit gemeldetem, aber nicht zuordenbarem Besitzer. */
  unresolvedHolders: number;
  /** Geräte, die zu einer Asset Collection gehören. */
  inCollections: number;
  /** Anzahl verschiedener Kollektionen. */
  collections: number;
}

/** Eine Asset Collection – ein Tisch, ein Meetingraum – mit ihrer Zusammensetzung. */
export interface AssetCollectionRow {
  path: string;
  /** „Tische“, „Meetingrooms“; null, wenn der Pfad keine Gruppe hat. */
  group: string | null;
  /** „Tisch-0001“, „Raum Amsel“ */
  name: string;
  count: number;
  byDeviceType: Record<string, number>;
  byStatus: Partial<Record<AssetStatus, number>>;
  locations: string[];
  costCenters: string[];
  /** Personen, auf die Geräte der Kollektion gebucht sind – dann ist sie ausgeliehen. */
  holders: Array<{ accountId: string; displayName: string | null; mail: string | null; userPrincipalName: string | null }>;
}

export interface AssetCollectionsResponse {
  total: number;
  rows: AssetCollectionRow[];
}

export interface AssetUpdateInput {
  name?: string;
  assetTag?: string | null;
  serialNumber?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  deviceType?: string | null;
  status?: AssetStatus;
  catalogItemId?: string | null;
  accountId?: string | null;
  location?: string | null;
  costCenter?: string | null;
  assignedAt?: string | null;
  purchaseDate?: string | null;
  warrantyEnd?: string | null;
  purchaseCost?: number | null;
  currency?: string | null;
  notes?: string | null;
}

/**
 * Warum eine Lizenz als verwaist gilt. Die Reihenfolge ist die der
 * Dringlichkeit: Die ersten beiden sind eindeutig, die letzten beiden sind zu
 * prüfen und nicht zu kündigen.
 */
export const ORPHAN_REASONS = ['DIRECTORY_DISABLED', 'DIRECTORY_REMOVED', 'NOT_IN_DIRECTORY', 'NO_ADDRESS'] as const;
export type OrphanReason = (typeof ORPHAN_REASONS)[number];

/** Eine Lizenz, deren Person im Verzeichnis nicht mehr aktiv ist. */
export interface OrphanRow {
  assignmentId: string;
  providerSlug: string;
  providerName: string;
  skuId: string;
  skuPartNumber: string;
  skuDisplayName: string;
  isFree: boolean;
  accountId: string;
  /** Name und Adresse, wie der ANBIETER sie führt. */
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
  department: string | null;
  countryCode: string;
  countryName: string;
  reason: OrphanReason;
  /** Das Verzeichniskonto, auf das sich die Aussage stützt. */
  directoryDisplayName: string | null;
  directoryMail: string | null;
  directoryLastSeenAt: string | null;
  assignedAt: string | null;
  lastSignInAt: string | null;
  monthlyCost: number | null;
  currency: string | null;
}

export interface OrphanTotals {
  count: number;
  monthlyCost: number;
  unpricedCount: number;
  currency: string | null;
  byReason: Record<OrphanReason, number>;
  /** Nur die eindeutigen Fälle – die Zahl, mit der man rechnen darf. */
  actionableCount: number;
  actionableMonthlyCost: number;
}

export interface OrphanReport {
  /** false = keine Verzeichnisquelle angebunden; dann ist keine Aussage möglich. */
  directoryAvailable: boolean;
  directoryProviders: Array<{
    slug: string;
    displayName: string;
    connectorKey: string;
    accountCount: number;
    lastSyncAt: string | null;
  }>;
  rows: OrphanRow[];
  totals: OrphanTotals;
}

/** true = das Verzeichnis kennt die Person und führt sie nicht mehr. */
export function isActionableOrphan(reason: OrphanReason): boolean {
  return reason === 'DIRECTORY_DISABLED' || reason === 'DIRECTORY_REMOVED';
}

/** Zustand einer Kataloganfrage. */
export const CATALOG_REQUEST_STATUS = [
  'AWAITING_APPROVAL',
  'PENDING',
  'SUBMITTED',
  'FAILED',
  'REJECTED',
  'CANCELLED',
] as const;
export type CatalogRequestStatus = (typeof CATALOG_REQUEST_STATUS)[number];

/** Eine Position einer Anfrage – als Kopie des Katalogeintrags zum Zeitpunkt der Anfrage. */
export interface CatalogRequestItemRow {
  id: string;
  catalogItemId: string | null;
  name: string;
  manufacturer: string;
  modelNumber: string | null;
  quantity: number;
  price: string | null;
  currency: string | null;
  priceInterval: CatalogPriceInterval;
  note: string | null;
  orderFieldLabel: string | null;
  orderFieldValue: string | null;
}

/** Eine Person aus dem Verzeichnis – für die Auswahl der freigebenden Person. */
export interface DirectoryPerson {
  /** Objekt-Id im Verzeichnis; dieselbe Id, die im Token als oid steht. */
  externalId: string;
  displayName: string;
  mail: string | null;
  jobTitle: string | null;
  department: string | null;
}

export interface CatalogRequestRow {
  id: string;
  /** Fortlaufende Nummer – das, was man am Telefon nennt. */
  number: number;
  requesterName: string | null;
  requesterMail: string | null;
  requesterUpn: string | null;
  note: string | null;
  status: CatalogRequestStatus;
  /** Wer freigeben muss – als Kopie, damit sie auch später noch lesbar ist. */
  approverOid: string | null;
  approverName: string | null;
  approverMail: string | null;
  approverSource: 'MANAGER' | 'CHOSEN' | null;
  decidedAt: string | null;
  /** Begründung der Ablehnung; bei einer Freigabe optional. */
  decisionNote: string | null;
  ticketNumber: string | null;
  ticketUrl: string | null;
  /** Warum der letzte Einreichversuch scheiterte; nur für die Verwaltung gedacht. */
  lastError: string | null;
  attempts: number;
  submittedAt: string | null;
  createdAt: string;
  provider: { id: string; slug: string; displayName: string } | null;
  items: CatalogRequestItemRow[];
}

/**
 * Antwort auf das Absenden. `ticketCreated` ist der eigentliche Befund: Die
 * Anfrage ist in jedem Fall gespeichert, ein Ticket nicht in jedem Fall
 * entstanden.
 */
export interface CatalogRequestResult {
  request: CatalogRequestRow;
  ticketCreated: boolean;
  message: string | null;
}

/** Zustand eines Softwareantrags. */
export const SOFTWARE_REQUEST_STATUS = ['PENDING', 'SUBMITTED', 'FAILED', 'CANCELLED'] as const;
export type SoftwareRequestStatus = (typeof SOFTWARE_REQUEST_STATUS)[number];

/**
 * Zustand der Datenschutz-Vorprüfung. SKIPPED heißt „nicht eingerichtet“ und
 * ist etwas anderes als FAILED („versucht und schiefgegangen“) – der
 * Unterschied entscheidet, ob jemand etwas reparieren muss.
 */
export const PRIVACY_ASSESSMENT_STATUS = ['PENDING', 'READY', 'SKIPPED', 'FAILED'] as const;
export type PrivacyAssessmentStatus = (typeof PRIVACY_ASSESSMENT_STATUS)[number];

export const PRIVACY_RISK = ['LOW', 'MEDIUM', 'HIGH', 'UNKNOWN'] as const;
export type PrivacyRisk = (typeof PRIVACY_RISK)[number];

export type PersonalDataAnswer = 'YES' | 'NO' | 'UNKNOWN';
export type YesNoUnknown = 'YES' | 'NO' | 'UNKNOWN';

/** Betriebsart und Region – beide optional, Vorgabe „unbekannt“. */
export const HOSTING_MODELS = ['UNKNOWN', 'SAAS', 'ON_PREMISES', 'HYBRID'] as const;
export type HostingModel = (typeof HOSTING_MODELS)[number];
export const HOSTING_REGIONS = ['UNKNOWN', 'GERMANY', 'EU', 'USA', 'OTHER'] as const;
export type HostingRegion = (typeof HOSTING_REGIONS)[number];

export interface AssessmentSource {
  title: string;
  url: string;
}

export interface SoftwareRequestRow {
  id: string;
  number: number;
  requesterName: string | null;
  requesterMail: string | null;
  requesterUpn: string | null;
  softwareName: string;
  vendor: string | null;
  vendorUrl: string | null;
  purpose: string;
  alternatives: string | null;
  userCount: number | null;
  neededBy: string | null;
  estimatedCost: string | null;
  currency: string | null;
  costInterval: CatalogPriceInterval;
  personalData: PersonalDataAnswer;
  dataCategories: string | null;
  dataSubjects: string | null;
  note: string | null;
  hostingModel: HostingModel;
  hostingRegion: HostingRegion;
  ssoEntra: YesNoUnknown;
  assessmentStatus: PrivacyAssessmentStatus;
  assessmentRisk: PrivacyRisk | null;
  /** Der Text der Einschätzung – ein Entwurf, keine Freigabe. */
  assessmentText: string | null;
  assessmentModel: string | null;
  assessmentSources: AssessmentSource[] | null;
  assessmentError: string | null;
  assessedAt: string | null;
  status: SoftwareRequestStatus;
  ticketNumber: string | null;
  ticketUrl: string | null;
  lastError: string | null;
  attempts: number;
  submittedAt: string | null;
  createdAt: string;
  provider: { id: string; slug: string; displayName: string } | null;
}

export interface SoftwareRequestResult {
  request: SoftwareRequestRow;
  ticketCreated: boolean;
  message: string | null;
}

/** Was beim Antrag ausgefüllt wird. Pflicht sind nur Name und Zweck. */
export interface SoftwareRequestInput {
  softwareName: string;
  vendor?: string | null;
  vendorUrl?: string | null;
  purpose: string;
  alternatives?: string | null;
  userCount?: number | null;
  neededBy?: string | null;
  estimatedCost?: number | null;
  currency?: string | null;
  costInterval?: CatalogPriceInterval | null;
  personalData?: PersonalDataAnswer | null;
  dataCategories?: string | null;
  dataSubjects?: string | null;
  note?: string | null;
  hostingModel?: HostingModel | null;
  hostingRegion?: HostingRegion | null;
  ssoEntra?: YesNoUnknown | null;
}

/** Eine Lizenz der angemeldeten Person - ohne Preis, wie bei den Geraeten. */
export interface MyLicenseRow {
  assignmentId: string;
  providerSlug: string;
  providerName: string;
  skuPartNumber: string;
  skuDisplayName: string;
  isFree: boolean;
  assignedAt: string | null;
  accountMail: string | null;
  accountUpn: string | null;
}

export interface MyLicensesResponse {
  rows: MyLicenseRow[];
  total: number;
}

/* ---------------------------------------------------------------------------
   Antrag auf einen neuen Benutzer. Das Formular beschreibt der Server
   (GET /api/user-requests/form); die Oberflaeche rendert, was dort steht.
--------------------------------------------------------------------------- */

export type UserRequestFieldType = 'text' | 'textarea' | 'date' | 'yesno' | 'select' | 'multiselect' | 'person' | 'people';

export interface UserRequestField {
  key: string;
  label: string;
  type: UserRequestFieldType;
  required: boolean;
  hint?: string;
  options?: string[];
  visibleWhen?: { key: string; equals: string };
}

export interface UserRequestSection {
  key: string;
  title: string;
  description?: string;
  fields: UserRequestField[];
}

export interface UserRequestForm {
  version: string;
  sections: UserRequestSection[];
}

export interface UserRequestAnswer {
  section: string;
  key: string;
  label: string;
  type: UserRequestFieldType;
  value: string | string[] | boolean;
}

export interface UserRequestRow {
  id: string;
  number: number;
  requesterOid: string;
  requesterUpn: string | null;
  requesterName: string | null;
  requesterMail: string | null;
  formVersion: string;
  answers: UserRequestAnswer[];
  employeeName: string;
  firstWorkday: string | null;
  status: 'PENDING' | 'SUBMITTED' | 'FAILED' | 'CANCELLED';
  ticketId: number | null;
  ticketNumber: string | null;
  ticketUrl: string | null;
  lastError: string | null;
  attempts: number;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserRequestResult {
  request: UserRequestRow;
  ticketCreated: boolean;
  message: string | null;
}

/** Offene Aufgaben fuer die Glocke (GET /api/me/todos). */
export type TodoKind = 'approvals' | 'failedRequests';
export interface TodoItem {
  kind: TodoKind;
  count: number;
  path: string;
}
export interface TodoSummary {
  total: number;
  items: TodoItem[];
}
